import { Header } from "@/components/Header";
import { HomepagePreview } from "@/components/HomepagePreview";
import { Footer } from "@/components/Footer";
import { EditableImage } from "@/components/EditableImage";
import { supabase } from "@/integrations/supabase/client";
import { useState, useEffect } from "react";
import { SeoHead } from "@/components/SeoHead";
import { LocalBusinessStructuredData } from "@/components/StructuredData";

const Index = () => {
  const [heroSrc, setHeroSrc] = useState("/hero-home.webp");

  // Load hero from DB on mount
  useEffect(() => {
    const loadHero = async () => {
      const { data } = await supabase.
      from('site_images').
      select('image_url').
      eq('image_key', 'homepage-hero').
      eq('is_active', true).
      maybeSingle();
      if (data?.image_url) {
        setHeroSrc(data.image_url);
      }
    };
    loadHero();
  }, []);

  const handleHeroUpdate = async (newUrl: string) => {
    setHeroSrc(newUrl);
    await supabase.from('site_images').upsert({
      image_key: 'homepage-hero',
      image_url: newUrl,
      category: 'homepage',
      alt_text_fr: 'Vue panoramique de Rio de Janeiro - Roca Rio'
    }, { onConflict: 'image_key' });
  };

  const scrollToGuide = () => {
    const el = document.getElementById('presentation-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <SeoHead
        title="Excursions Rio de Janeiro | Guide Francophone Certifiée — Roca Rio"
        description="Excursions à Rio de Janeiro avec une guide française certifiée. Randonnées, favelas, spots secrets en petit groupe. Tourisme authentique et solidaire. Réponse WhatsApp sous 24h."
        canonical="https://rocario-cleacarioca.com/"
        keywords="excursions Rio de Janeiro, guide francophone Rio, tourisme solidaire Brésil, randonnées Rio, visite Rio, Pedra da Gávea, Corcovado, favelas Rio"
        ogImage={heroSrc}
        ogImageAlt="Vue panoramique de Rio de Janeiro avec le Pain de Sucre et le Corcovado" />
      
      
      <LocalBusinessStructuredData type="TravelAgency" />
      
      {/* Header hidden on hero, appears after scroll */}
      <Header hideOnHero />

      <main className="bg-background">
        {/* Hero Section — full screen, no header */}
        <section className="relative h-screen flex items-end overflow-hidden pb-20 sm:pb-24">
          {/* EditableImage = image visible + barre admin */}
          <div className="absolute inset-0">
            <EditableImage
              src={heroSrc}
              alt="Vue panoramique de Rio de Janeiro avec le Pain de Sucre et le Corcovado - Roca Rio"
              className="w-full h-full object-cover object-left sm:object-bottom"
              bucket="site-assets"
              path="homepage/hero.jpg"
              allowDelete={false}
              loading="eager"
              fetchPriority="high"
              aspectRatio={16 / 9}
              onImageUpdate={handleHeroUpdate} />
            
          </div>

          {/* Overlay subtil */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" style={{ zIndex: 1 }} />
          
          {/* Contenu split : titre à gauche, accroche + CTA à droite */}
          <div className="relative w-full container mx-auto px-4 sm:px-6 lg:px-8" style={{ zIndex: 2 }}>
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 lg:gap-12">
              {/* Gauche — Titre massif */}
              <div className="lg:max-w-[60%]">
                <p className="text-sm sm:text-base uppercase tracking-[0.3em] text-white/70 mb-3 animate-fade-in font-semibold font-montserrat">
                  par Cléa Carioca
                </p>
                <p className="text-[clamp(3rem,10vw,6rem)] font-league uppercase text-white drop-shadow-[0_4px_24px_rgba(0,0,0,0.6)] animate-scale-in leading-[0.95]" aria-hidden="true">
                  Roca Rio
                </p>
                <h1 className="sr-only">Excursions Rio de Janeiro — Guide Francophone Certifiée Roca Rio</h1>
              </div>
              
              {/* Droite — Accroche + CTA */}
              <div className="lg:max-w-sm space-y-5 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="w-12 h-px bg-roca-orange hidden lg:block" />
                <p className="text-lg sm:text-xl text-white/90 leading-relaxed drop-shadow-lg font-montserrat">
                  Vivez Rio autrement, voyagez avec du sens.
                </p>
                <button
                  onClick={scrollToGuide}
                  className="group inline-flex items-center gap-3 px-7 py-3.5 bg-roca-blue-dark text-white font-semibold font-montserrat rounded-lg hover:bg-roca-blue-dark/90 hover:scale-105 transition-all shadow-lg hover:shadow-2xl uppercase tracking-wide">
                  
                  C'est parti !
                  
                </button>
              </div>
            </div>
          </div>
        </section>
        
        <HomepagePreview />
      </main>
      <Footer />
    </div>);

};
export default Index;